/**
 * Import a file, export a file, and see what is waiting on the share.
 *
 * The screen a distributor whose ERP is a Windows application with no inbound
 * port actually uses. Three panels, and the decisions in them are about the
 * moment after the upload:
 *
 * **The report says what the reader assumed.** A French Excel writes `;`, an
 * English one `,`, and both put a byte-order mark on the front. "0 lignes
 * importées" with nothing else is the worst possible answer, so the dialect,
 * the sheet and the file's own columns are on screen — an operator can then
 * tell whether the file or the mapping is wrong.
 *
 * **"Nothing was written" gets two different sentences.** A re-uploaded file
 * is `unchanged` and correct; a file whose header nothing matched is `refused`
 * and needs a mapping. Rendering both as one line is how an operator ends up
 * re-uploading a file that was fine.
 *
 * **The import runs in the background.** The upload answers as soon as the
 * file is read and stored, and the screen follows the run until it is done —
 * a 60 000-line article master would otherwise hold the request for minutes
 * and time the browser out. The latest runs are listed, so an operator who
 * leaves the page finds theirs again; an unfinished one is picked back up.
 *
 * **The export is fetched, not linked.** It is behind a bearer token, and an
 * `<a href>` the browser follows carries no Authorization header — it would
 * land on the sign-in page and download the HTML.
 *
 * A fourth panel, the data reset, appears only where the deployment enables it
 * and the user holds `maintenance.data.reset`; see `DataResetPanel`.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCan } from "@hardauto/core-access";
import type { ApiClient } from "@hardauto/core-api";
import { download, useAction, useResource } from "@hardauto/core-data";
import { EM_DASH, moment, quantity } from "@hardauto/core-i18n";
import { Field } from "@hardauto/core-forms";
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  DataTable,
  EmptyState,
  Input,
  Page,
  Select,
  Stat,
  StatGrid,
  useToast,
  type BadgeTone,
  type Column,
} from "@hardauto/core-ui";

import {
  ConnectorFilesApi,
  isFinished,
  EXPORT_OPERATORS,
  operatorTakesValue,
  type ExportFilter,
  type ExportOperator,
  type ImportReport,
  type ImportRun,
  type ImportStatus,
  type SpooledFile,
} from "../api";
import { NAMESPACE } from "../i18n";
import { DataResetPanel } from "./DataResetPanel";
import "./exchange.css";

/** Delimiters the export offers, in the order the select shows them. */
const DELIMITERS = ["semicolon", "comma", "tab"] as const;

/** How often a running import is asked how far it has gone. */
const POLL_MS = 2000;

const STATUS_TONE: Record<ImportStatus, BadgeTone> = {
  queued: "neutral",
  running: "info",
  done: "success",
  failed: "danger",
};

export function ExchangeScreen({ client }: { client: ApiClient }) {
  const { t, i18n } = useTranslation(NAMESPACE);
  const api = useMemo(() => new ConnectorFilesApi(client), [client]);
  const toast = useToast();
  const action = useAction();
  const mayImport = useCan("integration.mapping.write");
  // An export is limited to what the person may read on its own screen.
  const mayExport: Record<string, boolean> = {
    article: useCan("referential.article.read"),
    family: useCan("referential.family.read"),
    site: useCan("referential.site.read"),
    company: useCan("referential.site.read"),
    price_scale: useCan("referential.pricing.read"),
    client: useCan("clients.client.read"),
    // The stock (one row per article and depot) and the four-level category tree.
    stock: useCan("referential.stock.read"),
    category: useCan("catalog.category.read"),
  };
  // An entity's name for a person: its code is what the server knows it by, not what to read.
  const entityLabel = (code: string) => t(`entity.${code}`, { defaultValue: code });
  const exportable = Object.keys(mayExport).filter((code) => mayExport[code]);

  const entitiesFetcher = useCallback(() => api.importable(), [api]);
  const entities = useResource(entitiesFetcher);

  const spoolFetcher = useCallback(() => api.spool(), [api]);
  const spool = useResource(spoolFetcher);

  const [entity, setEntity] = useState("");
  const [sheet, setSheet] = useState("");
  const [run, setRun] = useState<ImportRun | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const recentFetcher = useCallback(() => api.recentImports(), [api]);
  const recent = useResource(recentFetcher);
  const reloadRecent = recent.reload;
  const reloadSpool = spool.reload;

  // An unfinished run is picked back up on arrival: an operator who left the
  // page mid-import comes back to its progress, not to an empty form.
  const resumed = useRef(false);
  useEffect(() => {
    if (resumed.current || !recent.data) return;
    resumed.current = true;
    const unfinished = recent.data.items.find((item) => !isFinished(item));
    if (unfinished) setRun((current) => current ?? unfinished);
  }, [recent.data]);

  // Follow the run until it ends. One request at a time, each scheduled after
  // the previous answer, so a slow server is never asked twice at once. A
  // failed poll is retried on the next tick: the run is still going.
  useEffect(() => {
    if (!run || isFinished(run)) return;
    let cancelled = false;
    const timer = window.setTimeout(() => {
      api
        .importRun(run.id)
        .then((next) => {
          if (cancelled) return;
          setRun(next);
          if (isFinished(next)) {
            reloadRecent();
            reloadSpool();
          }
        })
        .catch(() => {
          if (!cancelled) setRun((current) => (current ? { ...current } : current));
        });
    }, POLL_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [api, run, reloadRecent, reloadSpool]);

  const [exportEntity, setExportEntity] = useState("");
  // The first entity they may export, until they choose one.
  const exportChoice = exportEntity || exportable[0] || "";
  const [delimiter, setDelimiter] = useState<(typeof DELIMITERS)[number]>("semicolon");
  // The columns the export carries (all until the user unticks some) and the
  // rows it keeps (all until they add a filter).
  const exportFieldsFetcher = useCallback(
    () => api.exportFields(exportEntity),
    [api, exportEntity],
  );
  const exportFields = useResource(exportFieldsFetcher);
  const available = exportFields.data?.fields ?? [];
  const [fieldsChosen, setFieldsChosen] = useState<string[]>([]);
  const [exportFilters, setExportFilters] = useState<ExportFilter[]>([]);
  useEffect(() => {
    setFieldsChosen(exportFields.data?.fields ?? []);
    setExportFilters([]);
  }, [exportFields.data]);

  const offered = entities.data?.items ?? [];
  const chosen = entity || offered[0] || "";

  const spoolColumns: Column<SpooledFile>[] = [
    {
      key: "name",
      header: t("spool.column.name"),
      render: (row) => <code className="hardauto-code">{row.name}</code>,
    },
    {
      key: "size",
      header: t("spool.column.size"),
      render: (row) => `${row.size} o`,
    },
    {
      key: "written",
      header: t("spool.column.written"),
      render: (row) => moment(row.written_at, i18n.language),
    },
  ];

  return (
    <Page title={t("title")} description={t("intro")} eyebrow={t("eyebrow")}>
      {mayImport ? (
        <Card title={t("import.title")} description={t("import.intro")}>
          {offered.length === 0 && !entities.loading ? (
            <Alert tone="info" title={t("import.noEntities")} />
          ) : null}

          {mayImport && offered.length > 0 ? (
            <form
              className="hardauto-exchange-form"
              onSubmit={(event) => {
                event.preventDefault();
                const file = fileInput.current?.files?.[0];
                if (!file || !chosen) return;
                void action.run("import", async () => {
                  try {
                    const queued = await api.importFile(chosen, file, sheet.trim() || undefined);
                    setRun(queued);
                    recent.reload();
                  } catch (error) {
                    // The message matters here more than anywhere else on this
                    // screen: the server refuses an unreadable file with the
                    // remedy in the sentence ("an older .xls has to be
                    // re-saved"), and a generic toast would throw it away.
                    toast.show(error instanceof Error ? error.message : t("error.title"), {
                      tone: "danger",
                    });
                  }
                });
              }}
            >
              <Field
                label={t("import.entity")}
                hint={chosen === "category" ? t("import.treeHint") : t("import.entityHint")}
              >
                {(props) => (
                  <Select
                    {...props}
                    value={chosen}
                    onChange={(event) => setEntity(event.target.value)}
                  >
                    {offered.map((code) => (
                      <option key={code} value={code}>
                        {entityLabel(code)}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <Field label={t("import.file")}>
                {(props) => (
                  <Input
                    {...props}
                    ref={fileInput}
                    type="file"
                    accept=".csv,.tsv,.txt,.xlsx,.xlsm"
                  />
                )}
              </Field>
              <Field label={t("import.sheet")} hint={t("import.sheetHint")}>
                {(props) => (
                  <Input
                    {...props}
                    value={sheet}
                    onChange={(event) => setSheet(event.target.value)}
                  />
                )}
              </Field>
              <div>
                <Button
                  type="submit"
                  variant="primary"
                  busy={action.isBusy("import")}
                  busyLabel={t("import.uploading")}
                >
                  {t("import.run")}
                </Button>
              </div>
            </form>
          ) : null}

          {run ? <ImportProgress run={run} /> : null}

          {recent.data && recent.data.items.length > 0 ? (
            <RecentImports runs={recent.data.items} current={run} onShow={setRun} />
          ) : null}
        </Card>
      ) : null}

      <Card title={t("export.title")} description={t("export.intro")}>
        <div className="hardauto-exchange-form">
          <Field
            label={t("export.entity")}
            hint={
              exportChoice === "category"
                ? t("export.treeHint")
                : exportChoice === "stock"
                  ? t("export.stockHint")
                  : undefined
            }
          >
            {(props) => (
              <Select
                {...props}
                value={exportChoice}
                onChange={(event) => setExportEntity(event.target.value)}
              >
                {exportable.map((code) => (
                  <option key={code} value={code}>
                    {entityLabel(code)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label={t("export.delimiter")}>
            {(props) => (
              <Select
                {...props}
                value={delimiter}
                onChange={(event) =>
                  setDelimiter(event.target.value as (typeof DELIMITERS)[number])
                }
              >
                {DELIMITERS.map((value) => (
                  <option key={value} value={value}>
                    {t(`export.${value}`)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <fieldset className="hardauto-exchange-fields">
            <legend>{t("export.fields")}</legend>
            <div className="hardauto-exchange-fields__actions">
              <Button size="sm" variant="ghost" onClick={() => setFieldsChosen(available)}>
                {t("export.allFields")}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setFieldsChosen([])}>
                {t("export.noFields")}
              </Button>
            </div>
            <div className="hardauto-exchange-fields__list">
              {available.map((name) => (
                <Checkbox
                  key={name}
                  label={name}
                  checked={fieldsChosen.includes(name)}
                  onChange={(event) =>
                    setFieldsChosen((current) =>
                      event.target.checked
                        ? available.filter((entry) => entry === name || current.includes(entry))
                        : current.filter((entry) => entry !== name),
                    )
                  }
                />
              ))}
            </div>
          </fieldset>
          <fieldset className="hardauto-exchange-fields">
            <legend>{t("export.filters")}</legend>
            {exportFilters.map((entry, index) => (
              <div key={index} className="hardauto-exchange-filter">
                <Select
                  aria-label={t("export.filterColumn")}
                  value={entry.column}
                  onChange={(event) =>
                    setExportFilters((current) =>
                      current.map((item, at) =>
                        at === index ? { ...item, column: event.target.value } : item,
                      ),
                    )
                  }
                >
                  {available.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </Select>
                <Select
                  aria-label={t("export.filterOperator")}
                  value={entry.operator}
                  onChange={(event) =>
                    setExportFilters((current) =>
                      current.map((item, at) =>
                        at === index
                          ? { ...item, operator: event.target.value as ExportOperator }
                          : item,
                      ),
                    )
                  }
                >
                  {EXPORT_OPERATORS.map((operator) => (
                    <option key={operator} value={operator}>
                      {t(`export.operator.${operator}`)}
                    </option>
                  ))}
                </Select>
                {operatorTakesValue(entry.operator) ? (
                  <Input
                    aria-label={t("export.filterText")}
                    placeholder={
                      entry.operator === "in_list"
                        ? t("export.filterListPlaceholder")
                        : t("export.filterText")
                    }
                    value={entry.text}
                    onChange={(event) =>
                      setExportFilters((current) =>
                        current.map((item, at) =>
                          at === index ? { ...item, text: event.target.value } : item,
                        ),
                      )
                    }
                  />
                ) : (
                  // Nothing to type: the operator is the whole condition. A spacer keeps the
                  // row's columns where they are when the operator changes.
                  <span aria-hidden="true" />
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() =>
                    setExportFilters((current) => current.filter((_, at) => at !== index))
                  }
                >
                  {t("export.removeFilter")}
                </Button>
              </div>
            ))}
            <Button
              size="sm"
              disabled={available.length === 0}
              onClick={() =>
                setExportFilters((current) => [
                  ...current,
                  { column: available[0] ?? "", operator: "contains", text: "" },
                ])
              }
            >
              {t("export.addFilter")}
            </Button>
            <p className="hardauto-cell__muted">{t("export.filterHelp")}</p>
          </fieldset>
          <div>
            <Button
              variant="secondary"
              disabled={fieldsChosen.length === 0}
              busy={action.isBusy("export")}
              busyLabel={t("export.running")}
              onClick={() =>
                void action.run("export", async () => {
                  try {
                    const blob = await api.exportFile(exportChoice, delimiter, {
                      fields: fieldsChosen,
                      where: exportFilters,
                    });
                    download(blob, `${exportChoice}.csv`);
                    toast.show(t("export.downloaded"));
                  } catch {
                    toast.show(t("error.title"), { tone: "danger" });
                  }
                })
              }
            >
              {t("export.run")}
            </Button>
          </div>
        </div>
      </Card>

      {mayImport ? (
        <Card title={t("spool.title")} description={t("spool.intro")}>
          {spool.data && !spool.data.is_listable ? (
            <Alert tone="info" title={t("spool.notListable")} />
          ) : spool.data && spool.data.items.length === 0 ? (
            <EmptyState title={t("spool.empty")} />
          ) : (
            <DataTable<SpooledFile>
              caption={t("spool.title")}
              columns={spoolColumns}
              rows={spool.data?.items ?? []}
              rowKey={(row) => row.name}
              loading={spool.loading}
              loadingLabel={t("spool.title")}
              error={
                spool.failed
                  ? { title: t("error.load"), onRetry: spool.reload, retryLabel: t("error.retry") }
                  : undefined
              }
              empty={{ title: t("spool.empty") }}
            />
          )}
        </Card>
      ) : null}

      {/* Last, and only for those who may: emptying the tenant is the step
          before an import, never one taken on the way to it. */}
      <DataResetPanel
        client={client}
        onReset={() => {
          setRun(null);
          recent.reload();
          entities.reload();
          spool.reload();
        }}
      />
    </Page>
  );
}

/**
 * Where one run stands: progress while it goes, the report once it is done.
 */
function ImportProgress({ run }: { run: ImportRun }) {
  const { t, i18n } = useTranslation(NAMESPACE);
  const count = (value: number) => quantity(String(value), i18n.language);

  if (run.status === "failed") {
    return (
      <div className="hardauto-exchange-result">
        <Alert tone="danger" title={t("import.failedRun", { filename: run.report.filename })}>
          {run.error}
        </Alert>
        {run.processed > 0 ? <ImportResult report={run.report} /> : null}
      </div>
    );
  }

  if (run.status === "done") return <ImportResult report={run.report} />;

  const percent = run.total > 0 ? Math.floor((run.processed / run.total) * 100) : 0;
  return (
    <div className="hardauto-exchange-result" aria-live="polite">
      <p className="hardauto-exchange-progress__title">
        {run.status === "queued"
          ? t("import.queued", { filename: run.report.filename })
          : t("import.running", { filename: run.report.filename })}
      </p>
      {/* A native `progress`, not `Meter`: this is a task somebody is waiting
          on, and assistive technology announces the two differently. */}
      <progress
        className="hardauto-exchange-progress"
        max={run.total}
        value={run.processed}
        aria-label={t("import.progress")}
      >
        {percent} %
      </progress>
      <p className="hardauto-exchange-progress__count">
        {t("import.processed", { processed: count(run.processed), total: count(run.total) })}
      </p>
      <p className="hardauto-exchange-progress__hint">{t("import.background")}</p>
    </div>
  );
}

/** The latest runs, so a file uploaded earlier can be found again. */
function RecentImports({
  runs,
  current,
  onShow,
}: {
  runs: ImportRun[];
  current: ImportRun | null;
  onShow: (run: ImportRun) => void;
}) {
  const { t, i18n } = useTranslation(NAMESPACE);
  const count = (value: number) => quantity(String(value), i18n.language);

  const columns: Column<ImportRun>[] = [
    {
      key: "file",
      header: t("recent.column.file"),
      render: (row) => <code className="hardauto-code">{row.report.filename}</code>,
    },
    { key: "entity", header: t("recent.column.entity"), render: (row) => row.report.entity },
    {
      key: "status",
      header: t("recent.column.status"),
      render: (row) => <Badge tone={STATUS_TONE[row.status]}>{t(`status.${row.status}`)}</Badge>,
    },
    {
      key: "rows",
      header: t("recent.column.rows"),
      render: (row) => `${count(row.processed)} / ${count(row.total)}`,
    },
    {
      key: "at",
      header: t("recent.column.at"),
      render: (row) => (row.created_at ? moment(row.created_at, i18n.language) : EM_DASH),
    },
    {
      key: "show",
      header: "",
      render: (row) =>
        row.id === current?.id ? null : (
          <Button variant="ghost" size="sm" onClick={() => onShow(row)}>
            {t("recent.show")}
          </Button>
        ),
    },
  ];

  // The run on screen is fresher than the list, which reloads only when a run
  // ends: its row follows the progress bar.
  const rows = runs.map((row) => (current && row.id === current.id ? current : row));

  return (
    <div className="hardauto-exchange-result">
      <DataTable<ImportRun>
        caption={t("recent.title")}
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        loadingLabel={t("recent.title")}
        empty={{ title: t("recent.title") }}
      />
    </div>
  );
}

/**
 * What the file did, in the terms the operator asked in.
 *
 * Its own component because it is the half of this screen that gets read: the
 * form is used once and this is looked at until it makes sense.
 */
function ImportResult({ report }: { report: ImportReport }) {
  const { t } = useTranslation(NAMESPACE);

  return (
    <div className="hardauto-exchange-result">
      <StatGrid label={t("import.result")}>
        <Stat label={t("import.created")} value={String(report.created)} tone="success" />
        <Stat label={t("import.updated")} value={String(report.updated)} />
        <Stat label={t("import.unchanged")} value={String(report.unchanged)} />
        <Stat
          label={t("import.refused")}
          value={String(report.refused)}
          tone={report.refused > 0 ? "warning" : "neutral"}
        />
        <Stat
          label={t("import.failed")}
          value={String(report.failed)}
          tone={report.failed > 0 ? "danger" : "neutral"}
        />
      </StatGrid>

      {report.wrote_nothing ? (
        <Alert tone={report.anomalies > 0 ? "warning" : "info"} title={t("import.nothingWritten")}>
          {/* Two causes, two sentences. A re-uploaded file is correct and an
              unmatched header is not, and one line for both is how somebody
              re-uploads a file that was fine. */}
          {report.anomalies > 0
            ? t("import.nothingWrittenRefused")
            : t("import.nothingWrittenSame")}
        </Alert>
      ) : null}

      <dl className="hardauto-exchange-facts">
        <dt>{t("import.read")}</dt>
        <dd>{report.dialect}</dd>
        {report.sheet ? (
          <>
            <dt>{t("import.sheetRead")}</dt>
            <dd>{report.sheet}</dd>
          </>
        ) : null}
        <dt>{t("import.header")}</dt>
        <dd>
          <code className="hardauto-code">{report.header.join(" · ") || EM_DASH}</code>
        </dd>
        {report.unmapped.length > 0 ? (
          <>
            <dt>{t("import.unmapped")}</dt>
            <dd>
              {report.unmapped.map((name) => (
                <Badge key={name} tone="warning">
                  {name}
                </Badge>
              ))}
            </dd>
          </>
        ) : null}
        {report.examples.length > 0 ? (
          <>
            <dt>{t("import.examples")}</dt>
            <dd>
              <code className="hardauto-code">{report.examples.join(" · ")}</code>
            </dd>
          </>
        ) : null}
      </dl>
    </div>
  );
}
